import type { ShippingConfig, RateCard } from '../types';

export interface ShippingEstimate {
  amount: number;
  label: string;
}

export interface BuyerShowTotals {
  totalItemSpend: number;
  totalShippingPaid: number;
  itemSaleCount: number;
}

const AK_HI_PREFIXES = new Set(['967', '968', '995', '996', '997', '998', '999']);

/**
 * Calculate shipping estimate on the client side.
 * This is for display purposes during live auctions.
 * The actual shipping cost is calculated server-side when orders are created.
 */
export function calculateShippingEstimate(
  config: ShippingConfig | null,
  buyerZip: string | null,
  itemWeightOz: number = 16.0,
  buyerTotals?: BuyerShowTotals | null,
  currentItemPrice?: number
): ShippingEstimate {
  if (!config) {
    console.log('[SHIPPING_DEBUG] No config, defaulting to $9.99');
    return { amount: 9.99, label: 'Flat Rate' };
  }

  console.log('[SHIPPING_DEBUG] calculateShippingEstimate inputs:', {
    strategy: config.strategy,
    carrier: config.carrier,
    originZip: config.originZip,
    buyerZip,
    itemWeightOz,
    hasRateCard: !!config.rateCard,
    rateCardBandCount: config.rateCard?.bands?.length,
    flatRateAmount: config.flatRateAmount,
    rateAdjustmentType: config.rateAdjustmentType,
    rateAdjustmentAmount: config.rateAdjustmentAmount,
    freeShippingThresholdEnabled: config.freeShippingThresholdEnabled,
    freeShippingThresholdAmount: config.freeShippingThresholdAmount,
    maxShippingCapEnabled: config.maxShippingCapEnabled,
    maxShippingCapAmount: config.maxShippingCapAmount,
    buyerTotals,
    currentItemPrice,
  });

  // Check if free shipping threshold is already reached
  if (config.freeShippingThresholdEnabled && config.freeShippingThresholdAmount && buyerTotals) {
    const previousSpend = buyerTotals.totalItemSpend;
    const newTotalSpend = previousSpend + (currentItemPrice || 0);

    if (previousSpend >= config.freeShippingThresholdAmount) {
      // Already past threshold
      return { amount: 0, label: 'Free (threshold reached)' };
    }

    if (newTotalSpend >= config.freeShippingThresholdAmount) {
      // This item will cross the threshold
      return { amount: 0, label: 'Free (crosses threshold)' };
    }
  }

  // Check if max shipping cap is reached
  if (config.maxShippingCapEnabled && config.maxShippingCapAmount && buyerTotals) {
    const previousShipping = buyerTotals.totalShippingPaid;
    if (previousShipping >= config.maxShippingCapAmount) {
      return { amount: 0, label: 'Free (cap reached)' };
    }
  }

  // Calculate base shipping by strategy
  let baseEstimate: ShippingEstimate;

  switch (config.strategy) {
    case 'FREE':
      return { amount: 0, label: 'Free Shipping' };

    case 'FLAT_RATE': {
      const isFirstItem = !buyerTotals || buyerTotals.itemSaleCount === 0;
      let flatAmount: number;
      if (isFirstItem) {
        flatAmount = config.flatRateFirstItemAmount ?? config.flatRateAmount ?? 9.99;
      } else {
        flatAmount = config.flatRateAdditionalItemAmount ?? config.flatRateFirstItemAmount ?? config.flatRateAmount ?? 9.99;
      }
      baseEstimate = {
        amount: flatAmount,
        label: isFirstItem ? 'Flat Rate' : 'Flat Rate (add\'l)',
      };
      break;
    }

    case 'WEIGHT_BASED':
      baseEstimate = calculateWeightBasedEstimate(config, buyerZip, itemWeightOz);
      break;

    default:
      baseEstimate = { amount: 9.99, label: 'Flat Rate' };
  }

  // Apply rate adjustment (flat $ or percentage markup/markdown)
  baseEstimate = applyRateAdjustment(config, baseEstimate);

  // Apply max cap if needed
  if (config.maxShippingCapEnabled && config.maxShippingCapAmount && buyerTotals) {
    const previousShipping = buyerTotals.totalShippingPaid;
    const remainingCap = config.maxShippingCapAmount - previousShipping;
    if (baseEstimate.amount > remainingCap) {
      return {
        amount: Math.max(0, remainingCap),
        label: baseEstimate.label + ' (capped)',
      };
    }
  }

  return baseEstimate;
}

/**
 * Apply seller-configured rate adjustment (flat $ or percentage markup/markdown).
 * Mirrors Java ShippingCalculationService.applyRateAdjustment().
 */
function applyRateAdjustment(config: ShippingConfig, estimate: ShippingEstimate): ShippingEstimate {
  if (!config.rateAdjustmentType || config.rateAdjustmentAmount == null) {
    return estimate;
  }

  const adjustment = config.rateAdjustmentAmount;
  let adjusted: number;

  if (config.rateAdjustmentType === 'FLAT') {
    adjusted = Math.max(0, estimate.amount + adjustment);
  } else if (config.rateAdjustmentType === 'PERCENT') {
    const multiplier = 1 + adjustment / 100;
    adjusted = Math.max(0, Math.round(estimate.amount * multiplier * 100) / 100);
  } else {
    return estimate;
  }

  return { amount: adjusted, label: estimate.label };
}

/**
 * Calculate weight-based shipping estimate.
 */
function calculateWeightBasedEstimate(
  config: ShippingConfig,
  buyerZip: string | null,
  itemWeightOz: number
): ShippingEstimate {
  // Real carrier with rate card: use pre-computed lookup
  if (config.rateCard && buyerZip && config.originZip) {
    console.log('[SHIPPING_DEBUG] lookupRateCard inputs:', {
      originZip: config.originZip,
      buyerZip,
      itemWeightOz,
      bands: config.rateCard.bands.map(b => ({
        name: b.name,
        maxZipDistance: b.maxZipDistance,
        zipPrefixes: b.zipPrefixes,
        rateCount: b.rates.length,
        firstRate: b.rates[0],
        lastRate: b.rates[b.rates.length - 1],
      })),
    });
    const cost = lookupRateCard(config.rateCard, config.originZip, buyerZip, itemWeightOz);
    console.log('[SHIPPING_DEBUG] lookupRateCard result:', cost);
    if (cost !== null) {
      return {
        amount: Math.round(cost * 100) / 100,
        label: 'Estimated',
      };
    }
  } else {
    console.log('[SHIPPING_DEBUG] Skipping rate card lookup:', {
      hasRateCard: !!config.rateCard,
      buyerZip,
      originZip: config.originZip,
    });
  }

  // No rate card or no buyer ZIP: fallback
  console.log('[SHIPPING_DEBUG] Falling back to $9.99');
  return { amount: 9.99, label: 'Estimated' };
}

/**
 * Look up shipping cost from a pre-computed rate card.
 * Same algorithm as Java RateCardLookup for consistency.
 */
function lookupRateCard(
  rateCard: RateCard,
  originZip: string,
  buyerZip: string,
  weightOz: number
): number | null {
  if (!rateCard.bands || buyerZip.length < 3) {
    console.log('[SHIPPING_DEBUG] lookupRateCard early return: no bands or short buyerZip');
    return null;
  }

  const buyerPrefix = buyerZip.substring(0, 3);
  console.log('[SHIPPING_DEBUG] lookupRateCard: buyerPrefix=%s, weightOz=%d, weightOzCeil=%d', buyerPrefix, weightOz, Math.ceil(weightOz));

  // 1. Check zipPrefixes-based bands first (AK/HI)
  for (const band of rateCard.bands) {
    if (band.zipPrefixes && band.zipPrefixes.length > 0) {
      if (band.zipPrefixes.includes(buyerPrefix)) {
        const cost = findWeightRate(band.rates, weightOz);
        console.log('[SHIPPING_DEBUG] Matched zipPrefix band "%s", cost=%s', band.name, cost);
        if (cost !== null) return cost;
      }
    }
  }

  // 2. Compute ZIP prefix distance and match by maxZipDistance ascending
  const zipDistance = calculateZipDistance(originZip, buyerZip);
  console.log('[SHIPPING_DEBUG] zipDistance=%d (origin=%s, buyer=%s)', zipDistance, originZip, buyerZip);
  let bestBand: typeof rateCard.bands[0] | null = null;
  let nationalBand: typeof rateCard.bands[0] | null = null;

  for (const band of rateCard.bands) {
    // Skip prefix-based bands
    if (band.zipPrefixes && band.zipPrefixes.length > 0) continue;

    if (band.maxZipDistance !== null && band.maxZipDistance !== undefined) {
      if (zipDistance <= band.maxZipDistance) {
        if (!bestBand || band.maxZipDistance < (bestBand.maxZipDistance ?? Infinity)) {
          bestBand = band;
        }
      }
      if (!nationalBand || band.maxZipDistance > (nationalBand.maxZipDistance ?? 0)) {
        nationalBand = band;
      }
    } else {
      nationalBand = band;
    }
  }

  console.log('[SHIPPING_DEBUG] bestBand=%s, nationalBand=%s', bestBand?.name ?? 'null', nationalBand?.name ?? 'null');

  if (bestBand) {
    const cost = findWeightRate(bestBand.rates, weightOz);
    console.log('[SHIPPING_DEBUG] bestBand "%s" rate for %doz = %s', bestBand.name, weightOz, cost);
    if (cost !== null) return cost;
  }

  // Fallback: highest rate in national band
  if (nationalBand) {
    const cost = findHighestRate(nationalBand.rates);
    console.log('[SHIPPING_DEBUG] Fallback to national band "%s" highest rate = %s', nationalBand.name, cost);
    return cost;
  }

  console.log('[SHIPPING_DEBUG] No matching band found');
  return null;
}

function findWeightRate(rates: Array<{ maxOz: number; cost: number }>, weightOz: number): number | null {
  const weightOzCeil = Math.ceil(weightOz);
  for (const rate of rates) {
    if (weightOzCeil <= rate.maxOz) {
      return rate.cost;
    }
  }
  // If weight exceeds all brackets, use highest bracket
  if (rates.length > 0) {
    return rates[rates.length - 1].cost;
  }
  return null;
}

function findHighestRate(rates: Array<{ maxOz: number; cost: number }>): number | null {
  if (rates.length === 0) return null;
  let highest = 0;
  for (const rate of rates) {
    if (rate.cost > highest) highest = rate.cost;
  }
  return highest;
}

/**
 * Calculate a simple distance metric based on ZIP prefix difference.
 */
function calculateZipDistance(originZip: string, destZip: string): number {
  if (!originZip || !destZip || originZip.length < 3 || destZip.length < 3) {
    return Number.MAX_VALUE;
  }

  const originPrefix = parseInt(originZip.substring(0, 3), 10);
  const destPrefix = parseInt(destZip.substring(0, 3), 10);
  if (isNaN(originPrefix) || isNaN(destPrefix)) return Number.MAX_VALUE;
  return Math.abs(originPrefix - destPrefix);
}

/**
 * Check if a ZIP code belongs to Alaska or Hawaii.
 */
export function isAlaskaOrHawaii(zip: string): boolean {
  if (!zip || zip.length < 3) return false;
  return AK_HI_PREFIXES.has(zip.substring(0, 3));
}

/**
 * Format a shipping cost for display.
 */
export function formatShippingCost(amount: number): string {
  if (amount === 0) {
    return 'Free';
  }
  return `$${amount.toFixed(2)}`;
}

/**
 * Get the first-item flat rate after applying rate adjustments.
 * Used for display in shipping info cards.
 */
export function getAdjustedFlatRate(config: ShippingConfig): number {
  let rate = config.flatRateFirstItemAmount ?? config.flatRateAmount ?? 9.99;
  if (!config.rateAdjustmentType || config.rateAdjustmentAmount == null) {
    return rate;
  }
  if (config.rateAdjustmentType === 'FLAT') {
    return Math.max(0, rate + config.rateAdjustmentAmount);
  } else if (config.rateAdjustmentType === 'PERCENT') {
    const multiplier = 1 + config.rateAdjustmentAmount / 100;
    return Math.max(0, Math.round(rate * multiplier * 100) / 100);
  }
  return rate;
}

/**
 * Get the additional-item flat rate after applying rate adjustments.
 */
export function getAdjustedFlatRateAdditional(config: ShippingConfig): number {
  let rate = config.flatRateAdditionalItemAmount ?? config.flatRateFirstItemAmount ?? config.flatRateAmount ?? 9.99;
  if (!config.rateAdjustmentType || config.rateAdjustmentAmount == null) {
    return rate;
  }
  if (config.rateAdjustmentType === 'FLAT') {
    return Math.max(0, rate + config.rateAdjustmentAmount);
  } else if (config.rateAdjustmentType === 'PERCENT') {
    const multiplier = 1 + config.rateAdjustmentAmount / 100;
    return Math.max(0, Math.round(rate * multiplier * 100) / 100);
  }
  return rate;
}

/**
 * Get shipping info text for display under items.
 */
export function getShippingInfoText(config: ShippingConfig | null): string[] {
  if (!config) {
    return [];
  }

  const info: string[] = [];

  if (config.freeShippingThresholdEnabled && config.freeShippingThresholdAmount) {
    info.push(`Free shipping on orders over $${config.freeShippingThresholdAmount.toFixed(2)}`);
  }

  if (config.maxShippingCapEnabled && config.maxShippingCapAmount) {
    info.push(`Max shipping cap: $${config.maxShippingCapAmount.toFixed(2)}`);
  }

  if (config.originZip && config.strategy === 'WEIGHT_BASED') {
    info.push(`Ships from: ${config.originZip}`);
  }

  return info;
}
